import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  Activity,
  Clipboard,
  ExternalLink,
  Loader2,
  Maximize2,
  Minimize2,
  Monitor,
  Play,
  Settings2,
  Square,
  Terminal,
  Zap,
} from 'lucide-react';
import { mcpCall } from '../../lib/mcp';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  Select,
  StatusDot,
  Tab,
  TabList,
  TabPanel,
  Tabs,
  Toggle,
} from '../../components/ui';
import { toast } from 'sonner';

// ── Types ─────────────────────────────────────────────────────────────────────

interface AgentStatus {
  session: boolean;
  ttyd: boolean;
  running: boolean;
  command: string;
  startedAt: string | null;
  port: number;
}

interface StatusData {
  claudeCode: AgentStatus;
  qwenCode: AgentStatus;
  opencode: AgentStatus;
  claudeLocal: AgentStatus;
  kimiCode: AgentStatus;
}

interface Config {
  work_dir?: string;
  // Claude Code
  claude_model?: string;
  claude_api_key?: string;
  claude_daemon?: string;
  claude_thinking_budget?: string;
  claude_system_prompt?: string;
  // Qwen Code
  qwen_model?: string;
  qwen_api_key?: string;
  qwen_api_base?: string;
  qwen_bin_path?: string;
  qwen_extra_args?: string;
  qwen_daemon?: string;
  qwen_system_prompt?: string;
  qwen_approval_mode?: string;
  // Claude Code — auth
  claude_auth_mode?: string; // 'apikey' | 'oauth'
  // Open Code
  opencode_bin_path?: string;
  opencode_model?: string;
  opencode_api_base?: string;
  opencode_openai_key?: string;
  opencode_ollama_base?: string;
  opencode_daemon?: string;
  opencode_kimi_key?: string;
  opencode_approval_mode?: string;
  // Claude Local
  claude_local_base_url?: string;
  claude_local_model?: string;
  // Kimi Code
  kimi_api_key?: string;
  kimi_model?: string;
  kimi_approval_mode?: string;
  kimi_daemon?: string;
  kimi_extra_args?: string;
}

// ── ttyd terminál přes iframe ────────────────────────────────────────────────

const TERM_HEIGHT_MIN = 300;
const TERM_HEIGHT_DEF = 580;
const TERM_HEIGHT_FULL = 'calc(100vh - 140px)';

type BackendKey = 'claude-code' | 'qwen-code' | 'opencode' | 'claude-local' | 'kimi-code';

const BACKEND_PATH: Record<BackendKey, string> = {
  'qwen-code': '/term/localcode/qwen',
  'claude-code': '/term/localcode/claude',
  'opencode': '/term/localcode/opencode',
  'claude-local': '/term/localcode/claude-local',
  'kimi-code': '/term/localcode/kimi',
};

function TtydTerminal({ backend, ttydAlive }: { backend: BackendKey; ttydAlive: boolean }) {
  const { t } = useTranslation();
  const [fs, setFs] = useState(false);
  const [height, setHeight] = useState(TERM_HEIGHT_DEF);
  const [loaded, setLoaded] = useState(false);
  const [pasteOpen, setPasteOpen] = useState(false);
  const pasteRef = useRef<HTMLTextAreaElement>(null);
  const dragRef = useRef<{ startY: number; startH: number } | null>(null);

  // Reset loaded při každém novém spuštění ttyd
  useEffect(() => {
    if (!ttydAlive) setLoaded(false);
  }, [ttydAlive]);

  // Příjem textu z ttyd iframe přes postMessage → zapíše do clipboardu parent stránky
  // Na HTTPS používá clipboard API, fallback na execCommand
  useEffect(() => {
    const handler = (e: MessageEvent) => {
      if (e.data?.type !== 'ttyd-copy' || typeof e.data.text !== 'string') return;
      const text = e.data.text;
      if (navigator.clipboard?.writeText) {
        navigator.clipboard.writeText(text).then(() => {
          toast.success(t('localcode.copiedToast'), { duration: 1000 });
        }).catch(() => {
          // fallback
          const el = document.createElement('textarea');
          el.value = text;
          el.style.cssText = 'position:fixed;top:0;left:0;opacity:0;pointer-events:none';
          document.body.appendChild(el);
          el.focus();
          el.select();
          if (document.execCommand('copy')) toast.success(t('localcode.copiedToast'), { duration: 1000 });
          document.body.removeChild(el);
        });
      } else {
        const el = document.createElement('textarea');
        el.value = text;
        el.style.cssText = 'position:fixed;top:0;left:0;opacity:0;pointer-events:none';
        document.body.appendChild(el);
        el.focus();
        el.select();
        if (document.execCommand('copy')) toast.success(t('localcode.copiedToast'), { duration: 1000 });
        document.body.removeChild(el);
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, []);

  const openExternal = () => window.open(`${BACKEND_PATH[backend]}/`, `ttyd-${backend}`);

  // Otevři paste dialog — fallback když clipboard.readText() selže
  const openPasteBox = () => {
    setPasteOpen(true);
    setTimeout(() => pasteRef.current?.focus(), 50);
  };

  const handlePaste = () => {
    // Vždy otevři textarea — clipboard.readText() je nespolehlivé v iframe kontextu
    openPasteBox();
  };

  const submitPaste = async (text: string) => {
    if (text) await mcpCall('localcode.agent_paste', { agent: backend, text }, { silent: true }).catch(() => {});
    setPasteOpen(false);
  };

  // Drag-to-resize
  const onDragStart = (e: React.MouseEvent) => {
    e.preventDefault();
    dragRef.current = { startY: e.clientY, startH: height };
    const onMove = (ev: MouseEvent) => {
      if (!dragRef.current) return;
      setHeight(
        Math.max(TERM_HEIGHT_MIN, dragRef.current.startH + ev.clientY - dragRef.current.startY),
      );
    };
    const onUp = () => {
      dragRef.current = null;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  const iframeHeight = fs ? TERM_HEIGHT_FULL : `${height}px`;

  // ttyd neběží → nic nezobrazovat (start button v kartě to řeší)
  if (!ttydAlive) return null;

  return (
    <div className='relative' onContextMenu={(e) => e.preventDefault()}>
      {/* Toolbar — vždy viditelný pruh nad terminálem */}
      <div className='flex items-center gap-2 mb-1 px-1'>
        <button
          onClick={handlePaste}
          className='flex items-center gap-1 px-2 py-1 rounded bg-gray-800 border border-gray-700 text-gray-300 hover:text-white hover:bg-gray-700 text-xs'
        >
          <Clipboard size={12} /> {t('localcode.pasteButton')}
        </button>
        <button
          onClick={() => setFs((f) => !f)}
          className='flex items-center gap-1 px-2 py-1 rounded bg-gray-800 border border-gray-700 text-gray-300 hover:text-white hover:bg-gray-700 text-xs'
        >
          {fs ? <Minimize2 size={12} /> : <Maximize2 size={12} />} {fs ? t('localcode.shrinkLabel') : t('localcode.expandLabel')}
        </button>
        <button
          onClick={openExternal}
          className='flex items-center gap-1 px-2 py-1 rounded bg-gray-800 border border-gray-700 text-gray-300 hover:text-white hover:bg-gray-700 text-xs'
        >
          <ExternalLink size={12} /> {t('localcode.newWindowButton')}
        </button>
        <span className='text-[10px] text-gray-700 ml-auto'>{t('localcode.selectionCopiesHint')}</span>
      </div>

      {/* Paste textarea */}
      {pasteOpen && (
        <div className='mb-1 p-2 bg-gray-900 border border-gray-700 rounded-lg'>
          <textarea
            ref={pasteRef}
            rows={2}
            placeholder={t('localcode.pasteHintPlaceholder')}
            className='w-full bg-gray-950 border border-gray-700 rounded px-2 py-1 text-sm text-white font-mono resize-none focus:outline-none focus:border-blue-500/50'
            onKeyDown={async (e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                await submitPaste(e.currentTarget.value);
              }
              if (e.key === 'Escape') setPasteOpen(false);
            }}
            onBlur={(e) => {
              if (!e.currentTarget.value) setPasteOpen(false);
            }}
          />
        </div>
      )}

      {/* iframe MUSÍ mít rozměry už při mount — ttyd/xterm.js si při init měří
          velikost containeru. display:none → 0×0 → xterm attach na tmux
          s 0 cols × 0 rows → při zobrazení full redraw („přepisuje se").
          Spinner overlay absolute, pod ním iframe s validními rozměry. */}
      <div style={{ position: 'relative', height: iframeHeight, width: '100%' }}>
        <iframe
          key={backend}
          src={`${BACKEND_PATH[backend]}/`}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            border: 'none',
            borderRadius: '0.5rem',
            opacity: loaded ? 1 : 0,
            transition: 'opacity 0.2s',
          }}
          title={`ttyd-${backend}`}
          allow='clipboard-read; clipboard-write'
          onLoad={() => setLoaded(true)}
        />
        {!loaded && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              pointerEvents: 'none',
            }}
            className='flex items-center justify-center bg-gray-950 rounded-lg border border-gray-800 text-gray-600 text-sm gap-2'
          >
            <Loader2 size={14} className='animate-spin' /> {t('localcode.connectingLabel')}
          </div>
        )}
      </div>

      {!fs && (
        <>
          <div
            onMouseDown={onDragStart}
            className='h-2 cursor-ns-resize bg-transparent hover:bg-blue-500/20 rounded-b-lg transition-colors'
            title={t('localcode.dragResizeTooltip')}
          />
        </>
      )}
    </div>
  );
}

// ── Qwen Code — nastavení ─────────────────────────────────────────────────────

// Backendy do rozbalovátka. Ověřeno 2026-09-07 — všechny předchozí položky byly
// mrtvé: mířily na https:// (servery mluví plain http) a na Ollama :11434 resp.
// vLLM :8000, které na těch strojích neběží. Proto se v UI nedal vybrat model.
const QWEN_PRESETS = [
  {
    id: 'bozenka-qwen36',
    label: 'Boženka — qwen36-35b (llama.cpp)',
    api_base: 'http://10.118.200.18:8004/v1',
    key_hint: 'na',
  },
  {
    id: 'vladenka-flash-next',
    label: 'Vladěnka — Qwen3.8-Flash-Next (llama.cpp)',
    api_base: 'http://10.118.200.19:8014/v1',
    key_hint: 'na',
  },
  {
    id: 'dashscope',
    label: 'Alibaba DashScope (cloud)',
    api_base: '',
    key_hint: 'DASHSCOPE_API_KEY',
  },
  { id: 'custom', label: 'Vlastní…', api_base: '', key_hint: '' },
];

function QwenSettings({ cfg, onSave, saving }: {
  cfg: Config;
  onSave: (p: Partial<Config>) => void;
  saving: boolean;
}) {
  const { t } = useTranslation();
  const [local, setLocal] = useState<Partial<Config>>({});
  const [presetId, setPresetId] = useState('custom');
  const [models, setModels] = useState<string[]>([]);
  const [modelsLoading, setMl] = useState(false);
  const [wizardPending, setWiz] = useState(false);

  useEffect(() => {
    setLocal({});
  }, [cfg]);

  const val = (k: keyof Config) => ((k in local ? local[k] : cfg[k]) ?? '') as string;
  const set = (k: keyof Config) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setLocal((l) => ({ ...l, [k]: e.target.value }));

  const effectiveBase = val('qwen_api_base') ||
    (presetId !== 'custom' ? QWEN_PRESETS.find((p) => p.id === presetId)?.api_base ?? '' : '');

  async function loadModels() {
    setMl(true);
    try {
      const data = await mcpCall<{ ok: boolean; models: string[] }>(
        'localcode.models_list',
        effectiveBase ? { api_base: effectiveBase } : {},
      );
      if (!data.ok || !data.models?.length) throw new Error(t('localcode.noModelsError'));
      setModels(data.models);
    } catch (e) {
      toast.error(t('localcode.cannotLoadModelsError', { message: (e as Error).message }));
    } finally {
      setMl(false);
    }
  }

  function pickPreset(id: string) {
    setPresetId(id);
    setModels([]);
    const p = QWEN_PRESETS.find((x) => x.id === id);
    if (p && id !== 'custom') {
      setLocal((l) => ({ ...l, qwen_api_base: p.api_base }));
    }
  }

  const inp = (
    k: keyof Config,
    label: string,
    extra?: React.InputHTMLAttributes<HTMLInputElement>,
  ) => (
    <label className='block'>
      <span className='text-[11px] text-gray-500'>{label}</span>
      <input
        className='mt-0.5 w-full bg-gray-950 border border-gray-800 rounded-lg px-3 py-1.5 text-sm text-white focus:outline-none focus:border-orange-500/50'
        value={val(k)}
        onChange={set(k)}
        {...extra}
      />
    </label>
  );

  return (
    <div className='space-y-3 p-4 bg-gray-950/60 rounded-xl border border-gray-800/40'>
      <label className='block'>
        <span className='text-[11px] text-gray-500'>{t('localcode.backendLabel')}</span>
        <Select
          className='mt-0.5 w-full'
          value={presetId}
          onChange={(e) => pickPreset(e.target.value)}
        >
          {QWEN_PRESETS.map((p) => <option key={p.id} value={p.id}>{p.id === 'custom' ? t('localcode.customPresetLabel') : p.label}</option>)}
        </Select>
      </label>

      {/* Model */}
      <div>
        <div className='flex items-center justify-between mb-0.5'>
          <span className='text-[11px] text-gray-500'>{t('localcode.modelLabel')}</span>
          {effectiveBase && (
            <button
              type='button'
              onClick={loadModels}
              disabled={modelsLoading}
              className='text-[11px] text-orange-500 hover:text-orange-300 disabled:text-gray-600'
            >
              {modelsLoading ? t('localcode.loadingLabel') : t('localcode.loadFromServerButton')}
            </button>
          )}
        </div>
        {models.length > 0
          ? (
            <Select
              className='w-full'
              value={val('qwen_model')}
              onChange={(e) => setLocal((l) => ({ ...l, qwen_model: e.target.value }))}
            >
              <option value=''>{t('localcode.defaultOptionLabel')}</option>
              {models.map((m) => <option key={m} value={m}>{m}</option>)}
            </Select>
          )
          : (
            <input
              className='w-full bg-gray-950 border border-gray-800 rounded-lg px-3 py-1.5 text-sm text-white focus:outline-none focus:border-orange-500/50'
              value={val('qwen_model')}
              onChange={set('qwen_model')}
              placeholder={t('localcode.qwenModelPlaceholder')}
            />
          )}
      </div>

      {inp(
        'qwen_api_key',
        `${t('localcode.apiKeyLabel')} ${
          QWEN_PRESETS.find((p) => p.id === presetId)?.key_hint
            ? `(${QWEN_PRESETS.find((p) => p.id === presetId)?.key_hint})`
            : ''
        }`,
        { type: 'password', placeholder: t('localcode.apiKeyPlaceholderHint') },
      )}

      {(presetId === 'custom' || presetId === 'dashscope') && (
        inp('qwen_api_base', t('localcode.apiBaseUrlLabel'), {
          placeholder: 'http://host:port/v1',
        })
      )}
      {presetId !== 'custom' && presetId !== 'dashscope' && (
        <div className='text-[11px] text-gray-600 font-mono pl-1'>
          api_base:{' '}
          <span className='text-gray-400'>
            {QWEN_PRESETS.find((p) => p.id === presetId)?.api_base}
          </span>
        </div>
      )}

      <div className='border-t border-gray-800/60 pt-3 space-y-2'>
        {inp('work_dir', t('localcode.workingDirectoryLabel'), { placeholder: '/opt/acm' })}
        {inp('qwen_bin_path', t('localcode.qwenBinPathLabel'), {
          placeholder: 'qwen  (npm: @qwen-code/qwen-code)',
        })}
        {inp('qwen_extra_args', t('localcode.extraArgsLabel'), { placeholder: '' })}

        <label className='block'>
          <span className='text-[11px] text-gray-500'>{t('localcode.approvalModeLabel')}</span>
          <Select
            className='mt-0.5 w-full'
            value={val('qwen_approval_mode') || 'yolo'}
            onChange={(e) => setLocal((l) => ({ ...l, qwen_approval_mode: e.target.value }))}
          >
            <option value='yolo'>{t('localcode.approvalYoloQwen')}</option>
            <option value='auto-edit'>{t('localcode.approvalAutoEdit')}</option>
            <option value='default'>{t('localcode.approvalDefault')}</option>
            <option value='plan'>{t('localcode.approvalPlan')}</option>
          </Select>
        </label>

        <label className='block'>
          <span className='text-[11px] text-gray-500'>
            {t('localcode.systemPromptLabel')} <span className='text-gray-600'>{t('localcode.emptyDefaultAcmPromptHint')}</span>
          </span>
          <textarea
            rows={8}
            className='mt-0.5 w-full bg-gray-950 border border-gray-800 rounded-lg px-3 py-1.5 text-sm text-white font-mono focus:outline-none focus:border-orange-500/50 resize-y'
            placeholder={t('localcode.qwenSystemPromptPlaceholder')}
            value={val('qwen_system_prompt')}
            onChange={(e) => setLocal((l) => ({ ...l, qwen_system_prompt: e.target.value }))}
          />
        </label>
      </div>

      <div className='flex items-center gap-2 pt-1'>
        <Button
          variant='warning'
          loading={saving}
          disabled={Object.keys(local).length === 0}
          onClick={() => onSave(local)}
        >
          {t('localcode.saveButton')}
        </Button>
        <Button
          variant='ghost'
          loading={wizardPending}
          title={t('localcode.wizardTooltip')}
          onClick={async () => {
            setWiz(true);
            try {
              await mcpCall('localcode.qwen_wizard_start');
              toast.success(t('localcode.wizardStartedToast'));
            } catch (e) {
              toast.error(t('localcode.wizardFailedError', { message: (e as Error).message }));
            } finally {
              setWiz(false);
            }
          }}
        >
          {t('localcode.startWizardButton')}
        </Button>
        <span className='text-[11px] text-gray-600 ml-1'>
          {t('localcode.manualAuthResetHint')}
        </span>
      </div>
    </div>
  );
}

// ── Claude OAuth code bar ────────────────────────────────────────────────────

function OauthCodeBar({ onDone }: { onDone: () => void }) {
  const { t } = useTranslation();
  const [code, setCode] = useState('');
  const [pending, setPending] = useState(false);
  const [authUrl, setAuthUrl] = useState<string | null>(null);

  // Polluj URL z tmux pane dokud se neobjeví
  useEffect(() => {
    let active = true;
    const poll = async () => {
      for (let i = 0; i < 30 && active; i++) {
        await new Promise((r) => setTimeout(r, 2000));
        try {
          const res = await mcpCall<{ url: string | null }>('localcode.claude_oauth_url_get', {}, { silent: true });
          if (res?.url && active) {
            setAuthUrl(res.url);
            return;
          }
        } catch { /* ignore */ }
      }
    };
    poll();
    return () => {
      active = false;
    };
  }, []);

  const submit = async () => {
    const c = code.trim();
    if (!c) return;
    setPending(true);
    try {
      await mcpCall('localcode.claude_oauth_code_submit', { code: c });
      setCode('');
      toast.success(t('localcode.codeSubmittedToast'));
      onDone();
    } catch (e) {
      toast.error(t('localcode.failedError', { message: (e as Error).message }));
    } finally {
      setPending(false);
    }
  };

  return (
    <div className='px-3 py-2 border-b border-gray-800/60 space-y-2'>
      {/* URL pro autorizaci */}
      <div className='flex items-center gap-2'>
        {authUrl
          ? (
            <>
              <span className='text-[11px] text-gray-400'>{t('localcode.openInBrowserStep')}</span>
              <a
                href={authUrl}
                target='_blank'
                rel='noreferrer'
                className='text-xs text-blue-400 hover:text-blue-300 underline truncate max-w-xs'
              >
                {authUrl.slice(0, 60)}…
              </a>
            </>
          )
          : (
            <span className='text-[11px] text-gray-500 flex items-center gap-1'>
              <Loader2 size={11} className='animate-spin' /> {t('localcode.waitingForUrlLabel')}
            </span>
          )}
        <button onClick={onDone} className='ml-auto text-gray-600 hover:text-gray-400 text-xs px-1'>
          ✕
        </button>
      </div>
      {/* Vložení kódu */}
      <div className='flex gap-2 items-center'>
        <span className='text-[11px] text-gray-400 whitespace-nowrap'>{t('localcode.pasteCodeStep')}</span>
        <input
          autoFocus
          className='flex-1 bg-gray-950 border border-gray-700 rounded-lg px-3 py-1.5 text-sm text-white font-mono focus:outline-none focus:border-blue-500'
          placeholder={t('localcode.codePlaceholderHint')}
          value={code}
          onChange={(e) => setCode(e.target.value)}
          onKeyDown={async (e) => {
            if (e.key === 'Enter') await submit();
          }}
        />
        <Button variant='secondary' disabled={!code.trim()} loading={pending} onClick={submit}>
          {t('localcode.submitButton')}
        </Button>
      </div>
    </div>
  );
}

// ── Claude Code — nastavení ───────────────────────────────────────────────────

function ClaudeSettings({ cfg, onSave, saving, onOauthStart }: {
  cfg: Config;
  onSave: (p: Partial<Config>) => void;
  saving: boolean;
  onOauthStart?: () => void;
}) {
  const { t } = useTranslation();
  const [local, setLocal] = useState<Partial<Config>>({});
  const [oauthPending, setOAuth] = useState(false);
  const [oauthCode, setOauthCode] = useState('');
  const [codePending, setCodePend] = useState(false);
  useEffect(() => {
    setLocal({});
  }, [cfg]);

  const submitCode = async () => {
    const code = oauthCode.trim();
    if (!code) return;
    setCodePend(true);
    try {
      await mcpCall('localcode.claude_oauth_code_submit', { code });
      setOauthCode('');
      toast.success(t('localcode.codeSubmittedToast'));
    } catch (e) {
      toast.error(t('localcode.codeSubmitFailedError', { message: (e as Error).message }));
    } finally {
      setCodePend(false);
    }
  };

  const val = (k: keyof Config) => ((k in local ? local[k] : cfg[k]) ?? '') as string;
  const set =
    (k: keyof Config) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
      setLocal((l) => ({ ...l, [k]: e.target.value }));

  const authMode = val('claude_auth_mode') || 'apikey';

  return (
    <div className='space-y-3 p-4 bg-gray-950/60 rounded-xl border border-gray-800/40'>
      {/* Auth mode toggle */}
      <div>
        <span className='text-[11px] text-gray-500 block mb-1.5'>{t('localcode.authenticationLabel')}</span>
        <div className='flex gap-1'>
          <button
            type='button'
            onClick={() => setLocal((l) => ({ ...l, claude_auth_mode: 'apikey' }))}
            className={`px-3 py-1 rounded-lg text-xs font-medium transition-colors ${
              authMode === 'apikey'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
            }`}
          >
            {t('localcode.apiKeyOptionLabel')}
          </button>
          <button
            type='button'
            onClick={() => setLocal((l) => ({ ...l, claude_auth_mode: 'oauth' }))}
            className={`px-3 py-1 rounded-lg text-xs font-medium transition-colors ${
              authMode === 'oauth'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
            }`}
          >
            {t('localcode.oauthAccountOptionLabel')}
          </button>
        </div>
      </div>

      {/* API key mode */}
      {authMode === 'apikey' && (
        <label className='block'>
          <span className='text-[11px] text-gray-500'>{t('localcode.anthropicApiKeyLabel')}</span>
          <input
            type='password'
            className='mt-0.5 w-full bg-gray-950 border border-gray-800 rounded-lg px-3 py-1.5 text-sm text-white focus:outline-none focus:border-blue-500/50'
            value={val('claude_api_key')}
            onChange={set('claude_api_key')}
          />
        </label>
      )}

      {/* OAuth mode */}
      {authMode === 'oauth' && (
        <div className='space-y-2'>
          <div className='rounded-lg bg-blue-950/30 border border-blue-800/30 p-3 text-[12px] text-blue-300 space-y-1'>
            <p className='font-medium'>{t('localcode.loginViaClaudeAiLabel')}</p>
            <ol className='text-blue-400/80 space-y-0.5 list-decimal list-inside'>
              <li>{t('localcode.oauthStep1')}</li>
              <li>
                {t('localcode.oauthStep2Pre')} <strong className='text-blue-300'>{t('localcode.claudeTerminalRef')}</strong>{' '}
                {t('localcode.oauthStep2Post')}
              </li>
              <li>{t('localcode.oauthStep3')}</li>
              <li>
                {t('localcode.oauthStep4Pre')}{' '}
                <strong className='text-blue-300'>{t('localcode.oauthStep4Bold')}</strong>
              </li>
              <li>
                {t('localcode.oauthStep5')}{' '}
                <code className='font-mono'>~/.claude/auth.json</code>
              </li>
            </ol>
          </div>
          <div className='flex gap-2'>
            <Button
              variant='primary'
              loading={oauthPending}
              onClick={async () => {
                setOAuth(true);
                setOauthCode('');
                try {
                  await mcpCall('localcode.claude_oauth_start');
                  onOauthStart?.();
                } catch (e) {
                  toast.error(t('localcode.oauthWizardFailedError', { message: (e as Error).message }));
                } finally {
                  setOAuth(false);
                }
              }}
            >
              {t('localcode.startOauthWizardButton')}
            </Button>
          </div>
          <div className='space-y-1.5'>
            <span className='text-[11px] text-gray-500 block'>
              {t('localcode.oauthStep2Instruction')}
            </span>
            <div className='flex gap-2'>
              <input
                className='flex-1 bg-gray-950 border border-gray-700 rounded-lg px-3 py-1.5 text-sm text-white font-mono focus:outline-none focus:border-blue-500/50'
                placeholder={t('localcode.pasteCodeHerePlaceholder')}
                value={oauthCode}
                onChange={(e) => setOauthCode(e.target.value)}
                onKeyDown={async (e) => {
                  if (e.key === 'Enter') await submitCode();
                }}
              />
              <Button
                variant='secondary'
                disabled={!oauthCode.trim()}
                loading={codePending}
                onClick={submitCode}
              >
                {t('localcode.submitButton')}
              </Button>
            </div>
          </div>
        </div>
      )}

      <label className='block'>
        <span className='text-[11px] text-gray-500'>{t('localcode.modelLabel')}</span>
        <Select
          className='mt-0.5 w-full'
          value={val('claude_model') || 'claude-sonnet-4-6'}
          onChange={set('claude_model')}
        >
          <option value='claude-sonnet-4-6'>claude-sonnet-4-6</option>
          <option value='claude-opus-4-6'>claude-opus-4-6</option>
          <option value='claude-sonnet-4-5'>claude-sonnet-4-5</option>
          <option value='claude-haiku-4-5-20251001'>claude-haiku-4-5</option>
        </Select>
      </label>

      <div className='border-t border-gray-800/60 pt-3 space-y-2'>
        <label className='block'>
          <span className='text-[11px] text-gray-500'>{t('localcode.workingDirectoryLabel')}</span>
          <input
            className='mt-0.5 w-full bg-gray-950 border border-gray-800 rounded-lg px-3 py-1.5 text-sm text-white focus:outline-none focus:border-blue-500/50'
            value={val('work_dir')}
            onChange={set('work_dir')}
            placeholder='/opt/acm'
          />
        </label>
        <label className='block'>
          <span className='text-[11px] text-gray-500'>{t('localcode.extendedThinkingLabel')}</span>
          <input
            type='number'
            min={0}
            max={100000}
            step={1000}
            className='mt-0.5 w-full bg-gray-950 border border-gray-800 rounded-lg px-3 py-1.5 text-sm text-white focus:outline-none focus:border-blue-500/50'
            value={parseInt(val('claude_thinking_budget') || '0')}
            onChange={set('claude_thinking_budget')}
          />
        </label>
        <label className='block'>
          <span className='text-[11px] text-gray-500'>
            {t('localcode.systemPromptLabel')}{' '}
            <span className='text-gray-600'>{t('localcode.claudeMdAutoloadHint')}</span>
          </span>
          <textarea
            rows={6}
            className='mt-0.5 w-full bg-gray-950 border border-gray-800 rounded-lg px-3 py-1.5 text-sm text-white font-mono focus:outline-none focus:border-blue-500/50 resize-y'
            placeholder={t('localcode.optionalSystemPromptPlaceholder')}
            value={val('claude_system_prompt')}
            onChange={set('claude_system_prompt')}
          />
        </label>
      </div>

      <Button
        variant='primary'
        loading={saving}
        disabled={Object.keys(local).length === 0}
        onClick={() => onSave(local)}
      >
        {t('localcode.saveButton')}
      </Button>
    </div>
  );
}

// ── Open Code — nastavení ─────────────────────────────────────────────────────

const OPENCODE_PRESETS = [
  {
    id: 'bozka-gemma4',
    label: 'boženka — Gemma4-31B 256K :8006',
    api_base: 'https://10.118.200.18:8006/v1',
  },
  {
    id: 'bozka-r1',
    label: 'boženka — R1-Distill-70B Q8 :8008',
    api_base: 'https://10.118.200.18:8008/v1',
  },
  {
    id: 'bozka-qwen3',
    label: 'boženka — Qwen3-235B 256K :8007',
    api_base: 'https://10.118.200.18:8007/v1',
  },
  {
    id: 'gb10-cpp',
    label: 'gb10 — Qwen3-Coder-30B :8004',
    api_base: 'https://10.118.200.18:8004/v1',
  },
  {
    id: 'vladka-next',
    label: 'vladěnka — Qwen3-Coder-Next :8004',
    api_base: 'https://10.118.200.19:8004/v1',
  },
  {
    id: 'gb10-vl7b',
    label: 'gb10 — Qwen2.5-VL-7B :8005',
    api_base: 'https://10.118.200.18:8005/v1',
  },
  { id: 'ai2-ollama', label: 'ai2 — Ollama', api_base: '' },
  { id: 'kimi', label: 'KIMI — Moonshot AI (cloud)', api_base: 'https://api.moonshot.cn/v1' },
  { id: 'custom', label: 'Vlastní…', api_base: '' },
];

function OpencodeSettings({ cfg, onSave, saving }: {
  cfg: Config;
  onSave: (p: Partial<Config>) => void;
  saving: boolean;
}) {
  const { t } = useTranslation();
  const [local, setLocal] = useState<Partial<Config>>({});
  const [presetId, setPresetId] = useState('gb10-cpp');
  const [models, setModels] = useState<string[]>([]);
  const [modelsLoading, setMl] = useState(false);

  useEffect(() => {
    setLocal({});
  }, [cfg]);

  const val = (k: keyof Config) => ((k in local ? local[k] : cfg[k]) ?? '') as string;
  const set = (k: keyof Config) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setLocal((l) => ({ ...l, [k]: e.target.value }));

  const effectiveBase = (presetId !== 'custom' && presetId !== 'ai2-ollama')
    ? (OPENCODE_PRESETS.find((p) => p.id === presetId)?.api_base ?? val('opencode_api_base'))
    : val('opencode_api_base');

  async function loadModels() {
    setMl(true);
    const base = effectiveBase || val('opencode_ollama_base');
    try {
      const data = await mcpCall<{ ok: boolean; models: string[] }>(
        'localcode.models_list',
        base ? { api_base: base } : {},
      );
      if (!data.ok || !data.models?.length) throw new Error(t('localcode.noModelsError'));
      setModels(data.models);
    } catch (e) {
      toast.error(t('localcode.cannotLoadModelsError', { message: (e as Error).message }));
    } finally {
      setMl(false);
    }
  }

  function pickPreset(id: string) {
    setPresetId(id);
    setModels([]);
    const p = OPENCODE_PRESETS.find((x) => x.id === id);
    if (p && id !== 'custom') {
      setLocal((l) => ({ ...l, opencode_api_base: p.api_base }));
    }
  }

  const inp = (
    k: keyof Config,
    label: string,
    extra?: React.InputHTMLAttributes<HTMLInputElement>,
  ) => (
    <label className='block'>
      <span className='text-[11px] text-gray-500'>{label}</span>
      <input
        className='mt-0.5 w-full bg-gray-950 border border-gray-800 rounded-lg px-3 py-1.5 text-sm text-white focus:outline-none focus:border-violet-500/50'
        value={val(k)}
        onChange={set(k)}
        {...extra}
      />
    </label>
  );

  return (
    <div className='space-y-3 p-4 bg-gray-950/60 rounded-xl border border-gray-800/40'>
      {/* Backend preset */}
      <label className='block'>
        <span className='text-[11px] text-gray-500'>{t('localcode.localBackendLabel')}</span>
        <Select
          className='mt-0.5 w-full'
          value={presetId}
          onChange={(e) => pickPreset(e.target.value)}
        >
          {OPENCODE_PRESETS.map((p) => <option key={p.id} value={p.id}>{p.id === 'custom' ? t('localcode.customPresetLabel') : p.label}</option>)}
        </Select>
      </label>

      {/* API base — vždy editovatelné, preset jen předplní hodnotu */}
      {presetId === 'ai2-ollama'
        ? inp('opencode_ollama_base', t('localcode.ollamaHostLabel'), { placeholder: 'https://10.118.200.19:11434' })
        : presetId !== 'kimi' && (
          inp('opencode_api_base', t('localcode.apiBaseUrlOpenAiLabel'), {
            placeholder: 'http://host:port/v1',
          })
        )}

      {/* Model */}
      <div>
        <div className='flex items-center justify-between mb-0.5'>
          <span className='text-[11px] text-gray-500'>{t('localcode.modelLabel')}</span>
          <button
            type='button'
            onClick={loadModels}
            disabled={modelsLoading}
            className='text-[11px] text-violet-500 hover:text-violet-300 disabled:text-gray-600'
          >
            {modelsLoading ? t('localcode.loadingLabel') : t('localcode.loadFromServerButton')}
          </button>
        </div>
        {models.length > 0
          ? (
            <Select
              className='w-full'
              value={val('opencode_model')}
              onChange={(e) => setLocal((l) => ({ ...l, opencode_model: e.target.value }))}
            >
              <option value=''>{t('localcode.defaultOptionLabel')}</option>
              {models.map((m) => <option key={m} value={m}>{m}</option>)}
            </Select>
          )
          : (
            <input
              className='w-full bg-gray-950 border border-gray-800 rounded-lg px-3 py-1.5 text-sm text-white focus:outline-none focus:border-violet-500/50'
              value={val('opencode_model')}
              onChange={set('opencode_model')}
              placeholder={t('localcode.opencodeModelPlaceholder')}
            />
          )}
      </div>

      {/* OpenAI key — jen pro vlastní OpenAI-compatible backend */}
      {(presetId === 'custom') && (
        inp('opencode_openai_key', t('localcode.apiKeyIfRequiredLabel'), {
          type: 'password',
          placeholder: t('localcode.naOrEmptyPlaceholder'),
        })
      )}
      {/* KIMI API key */}
      {presetId === 'kimi' && (
        <div className='space-y-2'>
          {inp('opencode_kimi_key', t('localcode.kimiApiKeyLabel'), {
            type: 'password',
            placeholder: 'sk-... (moonshot.cn → API keys)',
          })}
          <div className='flex items-center gap-2'>
            <a
              href='https://platform.moonshot.cn/console/api-keys'
              target='_blank'
              rel='noreferrer'
              className='text-[11px] text-violet-400 hover:text-violet-300 underline'
            >
              {t('localcode.getKimiApiKeyLink')}
            </a>
            <span className='text-[11px] text-gray-600'>moonshot.cn</span>
          </div>
          <p className='text-[11px] text-gray-600'>
            {t('localcode.modelColonRecommendedHint')}{' '}
            <span className='font-mono text-gray-400'>
              {val('opencode_model') || 'moonshot-v1-128k'}
            </span>{' '}
            {t('localcode.recommendedModelsHint')}
          </p>
        </div>
      )}

      <div className='border-t border-gray-800/60 pt-3 space-y-2'>
        <label className='block'>
          <span className='text-[11px] text-gray-500'>{t('localcode.approvalModeLabel')}</span>
          <Select
            className='mt-0.5 w-full'
            value={val('opencode_approval_mode') || 'allow'}
            onChange={(e) => setLocal((l) => ({ ...l, opencode_approval_mode: e.target.value }))}
          >
            <option value='allow'>{t('localcode.approvalAllowOpencode')}</option>
            <option value='ask'>{t('localcode.approvalAskOpencode')}</option>
            <option value='deny'>{t('localcode.approvalDenyOpencode')}</option>
          </Select>
        </label>
        {inp('opencode_bin_path', t('localcode.opencodeBinPathLabel'), {
          placeholder: '/home/antivandal/.opencode/bin/opencode',
        })}
        {inp('work_dir', t('localcode.workingDirectoryLabel'), { placeholder: '/opt/acm' })}
      </div>

      <p className='text-[11px] text-gray-600'>
        {t('localcode.openCodeNoAnthropicHint')}
      </p>

      <Button
        variant='secondary'
        loading={saving}
        disabled={Object.keys(local).length === 0}
        onClick={() => onSave(local)}
      >
        {t('localcode.saveButton')}
      </Button>
    </div>
  );
}

// ── Claude Local — nastavení ─────────────────────────────────────────────────

// Ověřeno 2026-09-07 proti /v1/models na obou strojích. Předchozí seznam byl celý
// mrtvý — https:// proti plain http serverům a modely, které tam dávno neběží
// (Gemma4-31B :8006, R1-70B :8008, Qwen3-235B :8007, Qwen3-Coder-Next :8004).
const CLAUDE_LOCAL_PRESETS = [
  { id: 'vladenka-flash-next', label: 'Vladěnka — Qwen3.8-Flash-Next :8014', base: 'http://10.118.200.19:8014', model: 'flash-next' },
  { id: 'custom', label: 'Vlastní…', base: '', model: '' },
];

function ClaudeLocalSettings({ cfg, onSave, saving }: {
  cfg: Config;
  onSave: (p: Partial<Config>) => void;
  saving: boolean;
}) {
  const { t } = useTranslation();
  const [local, setLocal] = useState<Partial<Config>>({});
  const [models, setModels] = useState<string[]>([]);
  const [modelsLoading, setMl] = useState(false);

  useEffect(() => { setLocal({}); }, [cfg]);

  const val = (k: keyof Config) => ((k in local ? local[k] : cfg[k]) ?? '') as string;
  const set = (k: keyof Config) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setLocal((l) => ({ ...l, [k]: e.target.value }));

  function pickPreset(id: string) {
    const p = CLAUDE_LOCAL_PRESETS.find((x) => x.id === id);
    if (p && id !== 'custom') {
      setLocal((l) => ({ ...l, claude_local_base_url: p.base, claude_local_model: p.model }));
    }
    setModels([]);
  }

  async function loadModels() {
    const base = (val('claude_local_base_url') || '').replace(/\/+$/, '');
    if (!base) return;
    setMl(true);
    try {
      const data = await mcpCall<{ ok: boolean; models: string[] }>('localcode.models_list', { api_base: base });
      if (!data.ok || !data.models?.length) throw new Error(t('localcode.noModelsError'));
      setModels(data.models);
    } catch (e) {
      toast.error(t('localcode.cannotLoadModelsError', { message: (e as Error).message }));
    } finally {
      setMl(false);
    }
  }

  return (
    <div className='space-y-3 p-4 bg-gray-950/60 rounded-xl border border-gray-800/40'>
      <label className='block'>
        <span className='text-[11px] text-gray-500'>{t('localcode.presetLabel')}</span>
        <Select
          className='mt-0.5 w-full'
          defaultValue='custom'
          onChange={(e) => pickPreset(e.target.value)}
        >
          {CLAUDE_LOCAL_PRESETS.map((p) => <option key={p.id} value={p.id}>{p.id === 'custom' ? t('localcode.customPresetLabel') : p.label}</option>)}
        </Select>
      </label>
      <label className='block'>
        <span className='text-[11px] text-gray-500'>{t('localcode.apiBaseUrlPlainLabel')}</span>
        <input
          className='mt-0.5 w-full bg-gray-950 border border-gray-800 rounded-lg px-3 py-1.5 text-sm text-white focus:outline-none focus:border-green-500/50 font-mono'
          value={val('claude_local_base_url')}
          onChange={set('claude_local_base_url')}
          placeholder='https://10.118.200.18:8008/v1'
        />
      </label>
      <div>
        <div className='flex items-center justify-between mb-0.5'>
          <span className='text-[11px] text-gray-500'>{t('localcode.modelLabel')}</span>
          <button
            type='button'
            onClick={loadModels}
            disabled={modelsLoading || !val('claude_local_base_url')}
            className='text-[11px] text-green-500 hover:text-green-300 disabled:text-gray-600'
          >
            {modelsLoading ? t('localcode.loadingLabel') : t('localcode.loadFromServerButton')}
          </button>
        </div>
        {models.length > 0
          ? (
            <Select
              className='w-full'
              value={val('claude_local_model')}
              onChange={(e) => setLocal((l) => ({ ...l, claude_local_model: e.target.value }))}
            >
              <option value=''>{t('localcode.defaultOptionLabel')}</option>
              {models.map((m) => <option key={m} value={m}>{m}</option>)}
            </Select>
          )
          : (
            <input
              className='w-full bg-gray-950 border border-gray-800 rounded-lg px-3 py-1.5 text-sm text-white focus:outline-none focus:border-green-500/50'
              value={val('claude_local_model')}
              onChange={set('claude_local_model')}
              placeholder='r1-70b / qwen3-235b / qwen3-coder-next'
            />
          )}
      </div>
      <Button
        variant='success'
        loading={saving}
        disabled={Object.keys(local).length === 0}
        onClick={() => onSave(local)}
      >
        {t('localcode.saveButton')}
      </Button>
    </div>
  );
}

// ── Kimi Code settings ────────────────────────────────────────────────────────
//
// Kimi Code (code.kimi.com) — Moonshot AI coding agent. Model se vybírá jako
// alias z ~/.kimi-code/config.toml — providery uživatel přidá přes `kimi
// provider add <url>` nebo `kimi provider catalog` interaktivně v terminálu.
// Zde jen API key + alias + approval mode.

function KimiSettings({ cfg, onSave, saving }: {
  cfg: Config;
  onSave: (p: Partial<Config>) => void;
  saving: boolean;
}) {
  const { t } = useTranslation();
  const [local, setLocal] = useState<Partial<Config>>({});
  useEffect(() => { setLocal({}); }, [cfg]);

  const val = (k: keyof Config) => ((k in local ? local[k] : cfg[k]) ?? '') as string;
  const set = (k: keyof Config) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setLocal((l) => ({ ...l, [k]: e.target.value }));

  return (
    <div className='space-y-3 p-4 bg-gray-950/60 rounded-xl border border-gray-800/40'>
      <label className='block'>
        <span className='text-[11px] text-gray-500'>{t('localcode.moonshotApiKeyLabel')}</span>
        <input
          type='password'
          className='mt-0.5 w-full bg-gray-950 border border-gray-800 rounded-lg px-3 py-1.5 text-sm text-white focus:outline-none focus:border-pink-500/50 font-mono'
          value={val('kimi_api_key')}
          onChange={set('kimi_api_key')}
          placeholder={t('localcode.moonshotKeyPlaceholderHint')}
        />
      </label>
      <label className='block'>
        <span className='text-[11px] text-gray-500'>{t('localcode.modelAliasLabel')}</span>
        <input
          className='mt-0.5 w-full bg-gray-950 border border-gray-800 rounded-lg px-3 py-1.5 text-sm text-white focus:outline-none focus:border-pink-500/50 font-mono'
          value={val('kimi_model')}
          onChange={set('kimi_model')}
          placeholder='kimi-k2-turbo-preview / moonshot-v1-128k / …'
        />
        <span className='block mt-1 text-[10px] text-gray-500'>
          {t('localcode.providerRegisterHintPre')}{' '}
          <code className='text-pink-400'>kimi provider add &lt;url&gt;</code> {t('localcode.providerRegisterHintMid')}{' '}
          <code className='text-pink-400'>kimi provider catalog</code>.
        </span>
      </label>
      <label className='block'>
        <span className='text-[11px] text-gray-500'>{t('localcode.approvalModeLabel')}</span>
        <Select
          className='mt-0.5 w-full'
          value={val('kimi_approval_mode') || 'yolo'}
          onChange={set('kimi_approval_mode')}
        >
          <option value='yolo'>{t('localcode.approvalYoloKimi')}</option>
          <option value='auto'>{t('localcode.approvalAutoKimi')}</option>
          <option value='ask'>{t('localcode.approvalAskKimi')}</option>
        </Select>
      </label>
      <label className='block'>
        <span className='text-[11px] text-gray-500'>{t('localcode.extraCliArgsLabel')}</span>
        <input
          className='mt-0.5 w-full bg-gray-950 border border-gray-800 rounded-lg px-3 py-1.5 text-sm text-white focus:outline-none focus:border-pink-500/50 font-mono'
          value={val('kimi_extra_args')}
          onChange={set('kimi_extra_args')}
          placeholder={t('localcode.extraCliArgsPlaceholder')}
        />
      </label>
      <Button
        variant='success'
        loading={saving}
        disabled={Object.keys(local).length === 0}
        onClick={() => onSave(local)}
      >
        {t('localcode.saveButton')}
      </Button>
    </div>
  );
}

// ── Panel header ──────────────────────────────────────────────────────────────

function PanelHeader({
  label,
  subtitle,
  status,
  daemon,
  onDaemonToggle,
  daemonDisabled,
  gearOpen,
  onGear,
  onTerminal,
  onStart,
  onStop,
  onKill,
  terminalPending,
  startPending,
  stopPending,
  killPending,
  startDisabled,
  accentClass,
}: {
  label: string;
  subtitle?: string;
  status: AgentStatus | undefined;
  daemon: boolean;
  onDaemonToggle: (v: boolean) => void;
  daemonDisabled: boolean;
  gearOpen: boolean;
  onGear: () => void;
  onTerminal: () => void;
  onStart: () => void;
  onStop: () => void;
  onKill: () => void;
  terminalPending: boolean;
  startPending: boolean;
  stopPending: boolean;
  killPending: boolean;
  startDisabled?: boolean;
  accentClass: string;
}) {
  const { t } = useTranslation();
  const ttyd = status?.ttyd ?? false;
  const running = status?.running ?? false;

  return (
    <div className='flex items-center gap-2 px-4 py-3'>
      <StatusDot status={running ? 'running' : ttyd ? 'pending' : 'stopped'} />
      <span className={`font-semibold text-sm ${accentClass}`}>{label}</span>
      {subtitle && <span className='text-xs text-gray-500 truncate'>{subtitle}</span>}
      <div className='flex-1' />
      <Toggle
        value={daemon}
        label='daemon'
        onSave={onDaemonToggle}
        saving={daemonDisabled}
        title={t('localcode.persistentDaemonTooltip')}
      />
      <Button
        variant='ghost'
        size='icon'
        title={t('localcode.settingsTooltip')}
        onClick={onGear}
        className={gearOpen ? 'text-white bg-gray-700' : ''}
      >
        <Settings2
          size={14}
          className={`transition-transform duration-200 ${gearOpen ? 'rotate-90' : ''}`}
        />
      </Button>
      {ttyd && (
        <Button
          variant='ghost'
          size='icon'
          title={t('localcode.killTooltip')}
          loading={killPending}
          onClick={onKill}
          className='text-red-600 hover:text-red-400 hover:bg-red-950/30'
        >
          <Zap size={13} />
        </Button>
      )}
      {/* Stav 1: ttyd neběží → jen tlačítko Konzola */}
      {!ttyd && (
        <Button
          variant='secondary'
          size='sm'
          loading={terminalPending}
          icon={<Monitor size={11} />}
          onClick={onTerminal}
        >
          {t('localcode.consoleButton')}
        </Button>
      )}
      {/* Stav 2: ttyd běží, agent neběží → Spustit agenta */}
      {ttyd && !running && (
        <Button
          variant='success'
          size='sm'
          loading={startPending}
          disabled={startDisabled}
          icon={<Play size={11} />}
          onClick={onStart}
        >
          {t('localcode.startButtonLabel')}
        </Button>
      )}
      {/* Stav 3: agent běží → Zastavit */}
      {ttyd && running && (
        <Button
          variant='danger'
          size='sm'
          loading={stopPending}
          icon={<Square size={11} />}
          onClick={onStop}
        >
          {t('localcode.stopButtonLabel')}
        </Button>
      )}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function LocalCodePage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [qwenGear, setQwenGear] = useState(false);
  const [claudeGear, setClaudeGear] = useState(false);
  const [oauthBarOpen, setOauthBarOpen] = useState(false);
  const [opencodeGear, setOpencodeGear] = useState(false);
  const [clLocalGear, setClLocalGear] = useState(false);
  const [kimiGear, setKimiGear] = useState(false);
  // Lazy mount — iframe se vytvoří jen při prvním zobrazení záložky, pak zůstane v DOM
  const [mountedTabs, setMountedTabs] = useState<Set<string>>(new Set(['qwen']));
  const activateTab = (tab: string) => setMountedTabs((prev) => new Set([...prev, tab]));

  const { data: statusData } = useQuery<StatusData>({
    queryKey: ['lc-status'],
    queryFn: () => mcpCall<StatusData>('localcode.status_get', {}, { silent: true }),
    refetchInterval: 2000,
  });

  const { data: configData, isLoading: cfgLoading } = useQuery<{ config: Config }>({
    queryKey: ['lc-config'],
    queryFn: () => mcpCall<{ config: Config }>('localcode.config_get', {}, { silent: true }),
  });

  const cfg = configData?.config ?? {};

  const saveCfg = useMutation({
    mutationFn: (patch: Partial<Config>) => mcpCall('localcode.config_set', patch),
    onSuccess: () => {
      toast.success(t('localcode.savedToast'));
      qc.invalidateQueries({ queryKey: ['lc-config'] });
    },
    onError: (e) => toast.error((e as Error).message),
  });

  const inv = () => qc.invalidateQueries({ queryKey: ['lc-status'] });

  const qwenTerminal = useMutation({
    mutationFn: () => mcpCall('localcode.agent_terminal', { agent: 'qwen-code' }),
    onSuccess: () => {
      toast.success(t('localcode.consoleStartedToast', { name: 'Qwen' }));
      inv();
    },
    onError: (e) => toast.error((e as Error).message),
  });
  const qwenStart = useMutation({
    mutationFn: () => mcpCall('localcode.agent_start', { agent: 'qwen-code' }),
    onSuccess: () => {
      toast.success(t('localcode.startedToast', { name: 'Qwen Code' }));
      inv();
    },
    onError: (e) => toast.error((e as Error).message),
  });
  const qwenStop = useMutation({
    mutationFn: () => mcpCall('localcode.agent_stop', { agent: 'qwen-code' }),
    onSuccess: () => {
      toast.success(t('localcode.stoppedToast', { name: 'Qwen Code' }));
      inv();
    },
    onError: (e) => toast.error((e as Error).message),
  });
  const qwenKill = useMutation({
    mutationFn: () => mcpCall('localcode.agent_kill', { agent: 'qwen-code' }),
    onSuccess: () => {
      toast.success(t('localcode.sessionKilledToast', { name: 'Qwen' }));
      inv();
    },
    onError: (e) => toast.error((e as Error).message),
  });
  const claudeTerminal = useMutation({
    mutationFn: () => mcpCall('localcode.agent_terminal', { agent: 'claude-code' }),
    onSuccess: () => {
      toast.success(t('localcode.consoleStartedToast', { name: 'Claude' }));
      inv();
    },
    onError: (e) => toast.error((e as Error).message),
  });
  const claudeStart = useMutation({
    mutationFn: () => mcpCall('localcode.agent_start', { agent: 'claude-code' }),
    onSuccess: () => {
      toast.success(t('localcode.startedToast', { name: 'Claude Code' }));
      inv();
    },
    onError: (e) => toast.error((e as Error).message),
  });
  const claudeStop = useMutation({
    mutationFn: () => mcpCall('localcode.agent_stop', { agent: 'claude-code' }),
    onSuccess: () => {
      toast.success(t('localcode.stoppedToast', { name: 'Claude Code' }));
      inv();
    },
    onError: (e) => toast.error((e as Error).message),
  });
  const claudeKill = useMutation({
    mutationFn: () => mcpCall('localcode.agent_kill', { agent: 'claude-code' }),
    onSuccess: () => {
      toast.success(t('localcode.sessionKilledToast', { name: 'Claude' }));
      inv();
    },
    onError: (e) => toast.error((e as Error).message),
  });
  const opencodeTerminal = useMutation({
    mutationFn: () => mcpCall('localcode.agent_terminal', { agent: 'opencode' }),
    onSuccess: () => {
      toast.success(t('localcode.consoleStartedToast', { name: 'OpenCode' }));
      inv();
    },
    onError: (e) => toast.error((e as Error).message),
  });
  const opencodeStart = useMutation({
    mutationFn: () => mcpCall('localcode.agent_start', { agent: 'opencode' }),
    onSuccess: () => {
      toast.success(t('localcode.startedToast', { name: 'Open Code' }));
      inv();
    },
    onError: (e) => toast.error((e as Error).message),
  });
  const opencodeStop = useMutation({
    mutationFn: () => mcpCall('localcode.agent_stop', { agent: 'opencode' }),
    onSuccess: () => {
      toast.success(t('localcode.stoppedToast', { name: 'Open Code' }));
      inv();
    },
    onError: (e) => toast.error((e as Error).message),
  });
  const opencodeKill = useMutation({
    mutationFn: () => mcpCall('localcode.agent_kill', { agent: 'opencode' }),
    onSuccess: () => {
      toast.success(t('localcode.sessionKilledToast', { name: 'Open Code' }));
      inv();
    },
    onError: (e) => toast.error((e as Error).message),
  });

  const clLocalTerminal = useMutation({
    mutationFn: () => mcpCall('localcode.agent_terminal', { agent: 'claude-local' }),
    onSuccess: () => {
      toast.success(t('localcode.consoleStartedToast', { name: 'Claude Local' }));
      inv();
    },
    onError: (e) => toast.error((e as Error).message),
  });
  const clLocalStart = useMutation({
    mutationFn: () => mcpCall('localcode.agent_start', { agent: 'claude-local' }),
    onSuccess: () => {
      toast.success(t('localcode.startedToast', { name: 'Claude Local' }));
      inv();
    },
    onError: (e) => toast.error((e as Error).message),
  });
  const clLocalStop = useMutation({
    mutationFn: () => mcpCall('localcode.agent_stop', { agent: 'claude-local' }),
    onSuccess: () => {
      toast.success(t('localcode.stoppedToast', { name: 'Claude Local' }));
      inv();
    },
    onError: (e) => toast.error((e as Error).message),
  });
  const clLocalKill = useMutation({
    mutationFn: () => mcpCall('localcode.agent_kill', { agent: 'claude-local' }),
    onSuccess: () => {
      toast.success(t('localcode.sessionKilledToast', { name: 'Claude Local' }));
      inv();
    },
    onError: (e) => toast.error((e as Error).message),
  });

  const kimiTerminal = useMutation({
    mutationFn: () => mcpCall('localcode.agent_terminal', { agent: 'kimi-code' }),
    onSuccess: () => { toast.success(t('localcode.consoleStartedToast', { name: 'Kimi' })); inv(); },
    onError: (e) => toast.error((e as Error).message),
  });
  const kimiStart = useMutation({
    mutationFn: () => mcpCall('localcode.agent_start', { agent: 'kimi-code' }),
    onSuccess: () => { toast.success(t('localcode.startedToast', { name: 'Kimi Code' })); inv(); },
    onError: (e) => toast.error((e as Error).message),
  });
  const kimiStop = useMutation({
    mutationFn: () => mcpCall('localcode.agent_stop', { agent: 'kimi-code' }),
    onSuccess: () => { toast.success(t('localcode.stoppedToast', { name: 'Kimi Code' })); inv(); },
    onError: (e) => toast.error((e as Error).message),
  });
  const kimiKill = useMutation({
    mutationFn: () => mcpCall('localcode.agent_kill', { agent: 'kimi-code' }),
    onSuccess: () => { toast.success(t('localcode.sessionKilledToast', { name: 'Kimi' })); inv(); },
    onError: (e) => toast.error((e as Error).message),
  });

  const qwenStatus = statusData?.qwenCode;
  const claudeStatus = statusData?.claudeCode;
  const opencodeStatus = statusData?.opencode;
  const clLocalStatus = statusData?.claudeLocal;
  const kimiStatus = statusData?.kimiCode;

  const toggleDaemon =
    (key: keyof Config, running: boolean, start: () => void, stop: () => void) =>
    (enabled: boolean) => {
      saveCfg.mutate({ [key]: enabled ? 'true' : 'false' }, {
        onSuccess: () => {
          if (enabled && !running) start();
          else if (!enabled && running) stop();
        },
      });
    };

  return (
    <Tabs defaultTab='qwen' onTabChange={activateTab}>
      <TabList>
        <Tab id='qwen'>
          <Terminal size={14} className='inline mr-1 text-orange-400' />{t('localcode.tabQwenCode')}
        </Tab>
        <Tab id='claude'>
          <Terminal size={14} className='inline mr-1 text-blue-400' />{t('localcode.tabClaudeCode')}
        </Tab>
        <Tab id='opencode'>
          <Terminal size={14} className='inline mr-1 text-violet-400' />{t('localcode.tabOpenCode')}
        </Tab>
        <Tab id='claude-local'>
          <Terminal size={14} className='inline mr-1 text-green-400' />{t('localcode.tabClaudeLocal')}
        </Tab>
        <Tab id='kimi'>
          <Terminal size={14} className='inline mr-1 text-pink-400' />{t('localcode.tabKimiCode')}
        </Tab>
        <Tab id='status'>
          <Activity size={14} className='inline mr-1' />{t('localcode.tabStatus')}
        </Tab>
      </TabList>

      {/* ── Qwen Code tab ── */}
      <TabPanel id='qwen' keepMounted>
        <Card>
          <CardHeader className='py-0 px-0'>
            <PanelHeader
              label={t('localcode.tabQwenCode')}
              description={cfg.qwen_model || 'qwen2.5-coder'}
              status={qwenStatus}
              daemon={cfg.qwen_daemon === 'true'}
              onDaemonToggle={toggleDaemon('qwen_daemon', qwenStatus?.running ?? false, () =>
                qwenStart.mutate(), () =>
                qwenStop.mutate())}
              daemonDisabled={saveCfg.isPending}
              gearOpen={qwenGear}
              onGear={() =>
                setQwenGear((o) =>
                  !o
                )}
              onTerminal={() => qwenTerminal.mutate()}
              onStart={() => qwenStart.mutate()}
              onStop={() => qwenStop.mutate()}
              onKill={() => qwenKill.mutate()}
              terminalPending={qwenTerminal.isPending}
              startPending={qwenStart.isPending}
              stopPending={qwenStop.isPending}
              killPending={qwenKill.isPending}
              accentClass='text-orange-300'
            />
          </CardHeader>
          {qwenGear && !cfgLoading && (
            <div className='px-4 pb-3 border-b border-gray-800/60'>
              <QwenSettings
                cfg={cfg}
                onSave={(p) => saveCfg.mutate(p)}
                saving={saveCfg.isPending}
              />
            </div>
          )}
          <CardContent className='p-3'>
            {mountedTabs.has('qwen') && (
              <TtydTerminal backend='qwen-code' ttydAlive={qwenStatus?.ttyd ?? false} />
            )}
          </CardContent>
        </Card>
      </TabPanel>

      {/* ── Claude Code tab ── */}
      <TabPanel id='claude' keepMounted>
        <Card>
          <CardHeader className='py-0 px-0'>
            <PanelHeader
              label={t('localcode.tabClaudeCode')}
              description={cfg.claude_model ?? 'claude-sonnet-4-6'}
              status={claudeStatus}
              daemon={cfg.claude_daemon === 'true'}
              onDaemonToggle={toggleDaemon(
                'claude_daemon',
                claudeStatus?.running ?? false,
                () => claudeStart.mutate(),
                () => claudeStop.mutate(),
              )}
              daemonDisabled={saveCfg.isPending}
              gearOpen={claudeGear}
              onGear={() => setClaudeGear((o) => !o)}
              onTerminal={() => claudeTerminal.mutate()}
              onStart={() => claudeStart.mutate()}
              onStop={() => claudeStop.mutate()}
              onKill={() => claudeKill.mutate()}
              terminalPending={claudeTerminal.isPending}
              startPending={claudeStart.isPending}
              stopPending={claudeStop.isPending}
              killPending={claudeKill.isPending}
              startDisabled={!cfg.claude_api_key}
              accentClass='text-blue-300'
            />
          </CardHeader>
          {claudeGear && !cfgLoading && (
            <div className='px-4 pb-3 border-b border-gray-800/60'>
              <ClaudeSettings
                cfg={cfg}
                onSave={(p) => saveCfg.mutate(p)}
                saving={saveCfg.isPending}
                onOauthStart={() => {
                  setOauthBarOpen(true);
                  claudeTerminal.mutate();
                }}
              />
            </div>
          )}
          {oauthBarOpen && <OauthCodeBar onDone={() => setOauthBarOpen(false)} />}
          <CardContent className='p-3'>
            {mountedTabs.has('claude') && (
              <TtydTerminal backend='claude-code' ttydAlive={claudeStatus?.ttyd ?? false} />
            )}
          </CardContent>
        </Card>
      </TabPanel>

      {/* ── Open Code tab ── */}
      <TabPanel id='opencode' keepMounted>
        <Card>
          <CardHeader className='py-0 px-0'>
            <PanelHeader
              label={t('localcode.tabOpenCode')}
              description='opencode.ai'
              status={opencodeStatus}
              daemon={cfg.opencode_daemon === 'true'}
              onDaemonToggle={toggleDaemon(
                'opencode_daemon',
                opencodeStatus?.running ?? false,
                () => opencodeStart.mutate(),
                () => opencodeStop.mutate(),
              )}
              daemonDisabled={saveCfg.isPending}
              gearOpen={opencodeGear}
              onGear={() => setOpencodeGear((o) => !o)}
              onTerminal={() => opencodeTerminal.mutate()}
              onStart={() => opencodeStart.mutate()}
              onStop={() => opencodeStop.mutate()}
              onKill={() => opencodeKill.mutate()}
              terminalPending={opencodeTerminal.isPending}
              startPending={opencodeStart.isPending}
              stopPending={opencodeStop.isPending}
              killPending={opencodeKill.isPending}
              accentClass='text-violet-300'
            />
          </CardHeader>
          {opencodeGear && !cfgLoading && (
            <div className='px-4 pb-3 border-b border-gray-800/60'>
              <OpencodeSettings
                cfg={cfg}
                onSave={(p) => saveCfg.mutate(p)}
                saving={saveCfg.isPending}
              />
            </div>
          )}
          <CardContent className='p-3'>
            {mountedTabs.has('opencode') && (
              <TtydTerminal backend='opencode' ttydAlive={opencodeStatus?.ttyd ?? false} />
            )}
          </CardContent>
        </Card>
      </TabPanel>

      {/* ── Stav tab ── */}
      {/* ── Claude Local tab ── */}
      <TabPanel id='claude-local' keepMounted>
        <Card>
          <CardHeader className='py-0 px-0'>
            <PanelHeader
              label={t('localcode.tabClaudeLocal')}
              subtitle={cfg.claude_local_model || cfg.claude_local_base_url || t('localcode.localModelFallback')}
              status={clLocalStatus}
              daemon={false}
              onDaemonToggle={() => {}}
              daemonDisabled={true}
              gearOpen={clLocalGear}
              onGear={() => setClLocalGear((g) => !g)}
              onTerminal={() => clLocalTerminal.mutate()}
              onStart={() => clLocalStart.mutate()}
              onStop={() => clLocalStop.mutate()}
              onKill={() => clLocalKill.mutate()}
              terminalPending={clLocalTerminal.isPending}
              startPending={clLocalStart.isPending}
              stopPending={clLocalStop.isPending}
              killPending={clLocalKill.isPending}
              accentClass='text-green-400'
            />
          </CardHeader>
          {clLocalGear && !cfgLoading && (
            <div className='px-4 pb-3 border-b border-gray-800/60'>
              <ClaudeLocalSettings
                cfg={cfg}
                onSave={(p) => saveCfg.mutate(p)}
                saving={saveCfg.isPending}
              />
            </div>
          )}
          <CardContent className='p-0'>
            <TtydTerminal backend='claude-local' ttydAlive={clLocalStatus?.ttyd ?? false} />
          </CardContent>
        </Card>
      </TabPanel>

      {/* ── Kimi Code tab ── */}
      <TabPanel id='kimi' keepMounted>
        <Card>
          <CardHeader className='py-0 px-0'>
            <PanelHeader
              label={t('localcode.tabKimiCode')}
              subtitle={cfg.kimi_model || t('localcode.kimiSubtitleFallback')}
              status={kimiStatus}
              daemon={cfg.kimi_daemon === 'true'}
              onDaemonToggle={toggleDaemon(
                'kimi_daemon',
                kimiStatus?.running ?? false,
                () => kimiStart.mutate(),
                () => kimiStop.mutate(),
              )}
              daemonDisabled={saveCfg.isPending}
              gearOpen={kimiGear}
              onGear={() => setKimiGear((g) => !g)}
              onTerminal={() => kimiTerminal.mutate()}
              onStart={() => kimiStart.mutate()}
              onStop={() => kimiStop.mutate()}
              onKill={() => kimiKill.mutate()}
              terminalPending={kimiTerminal.isPending}
              startPending={kimiStart.isPending}
              stopPending={kimiStop.isPending}
              killPending={kimiKill.isPending}
              accentClass='text-pink-400'
            />
          </CardHeader>
          {kimiGear && !cfgLoading && (
            <div className='px-4 pb-3 border-b border-gray-800/60'>
              <KimiSettings
                cfg={cfg}
                onSave={(p) => saveCfg.mutate(p)}
                saving={saveCfg.isPending}
              />
            </div>
          )}
          <CardContent className='p-0'>
            <TtydTerminal backend='kimi-code' ttydAlive={kimiStatus?.ttyd ?? false} />
          </CardContent>
        </Card>
      </TabPanel>

      <TabPanel id='status'>
        <div className='space-y-3'>
          {([
            { label: t('localcode.tabQwenCode'), data: qwenStatus, accent: 'text-orange-400' },
            { label: t('localcode.tabClaudeCode'), data: claudeStatus, accent: 'text-blue-400' },
            { label: t('localcode.tabOpenCode'), data: opencodeStatus, accent: 'text-violet-400' },
            { label: t('localcode.tabClaudeLocal'), data: clLocalStatus, accent: 'text-green-400' },
            { label: t('localcode.tabKimiCode'), data: kimiStatus, accent: 'text-pink-400' },
          ] as const).map(({ label, data, accent }) => (
            <Card key={label}>
              <CardContent>
                <div className='flex items-center gap-2 mb-3'>
                  <StatusDot
                    status={data?.running ? 'running' : data?.ttyd ? 'pending' : 'stopped'}
                    withLabel
                    label={label}
                  />
                </div>
                {data
                  ? (
                    <dl className='text-xs text-gray-400 space-y-1 font-mono'>
                      <div>
                        session:{' '}
                        <span className={data.session ? accent : 'text-gray-600'}>
                          {data.session ? t('localcode.yesLabel') : t('localcode.noLabel')}
                        </span>
                      </div>
                      <div>
                        ttyd:{' '}
                        <span className={data.ttyd ? accent : 'text-gray-600'}>
                          {data.ttyd ? t('localcode.runningLabel') : t('localcode.stoppedLabel')}
                        </span>
                      </div>
                      <div>
                        process:{' '}
                        <span className={data.running ? accent : 'text-gray-600'}>
                          {data.running ? data.command : t('localcode.noLabel')}
                        </span>
                      </div>
                      <div>
                        port: <span className={accent}>{data.port}</span>
                      </div>
                    </dl>
                  )
                  : <p className='text-xs text-gray-500'>{t('localcode.notRunningLabel')}</p>}
              </CardContent>
            </Card>
          ))}
        </div>
      </TabPanel>
    </Tabs>
  );
}
